import styled from '@emotion/styled';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { Grid, Box, Select, MenuItem, Checkbox, ListItemText, Link } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridSortModel,
  gridClasses,
} from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, Controller, useFormContext } from 'react-hook-form';
import { HiOutlineCog, HiOutlineOfficeBuilding, HiReceiptTax } from 'react-icons/hi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  ClientDTO,
  getAllInvoiceTypes,
  getFullTextSearchString,
  getInvoiceList,
  IInvoiceType,
  InvoiceDTO,
  InvoiceStatus,
  invoiceStatusMap,
  getPresignedURLForGet,
  getInvoiceCandidateList,
  saveColumnSettings,
  ColumnSettingsDTO,
  ColumnSettings,
  getColumnSettings,
} from 'wisestep-api';
import {
  WsButton,
  WsFilterDialog,
  EmptyTeamsAndUsersIcon,
  getDateDifferenceInDay,
  ControlledTextInput,
  WsFilterDialogProps,
  WsIconText,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  getDateUsingFormat,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../context';
import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import { useInvoicingAuthz, InvoicingAction } from '../../../hooks/useInvoicingAuthz';
import { Page } from '../../../types/Page';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { InvoiceListing } from '../types/InvoiceListing';

import { CustomToolbar } from './DataGridCustomToolBar';

interface IInvoiceList {
  placementType: 'CONTRACTOR' | 'PERMANENT';
}

export const StyledSmartFilterPopover = styled(Box)(() => ({
  minWidth: '24rem',
  paddingLeft: '0.5rem',
  paddingTop: '1rem',
  paddingBottom: '1rem',
}));

const invoiceStatusArray = Object.values(InvoiceStatus).filter(
  (v) => !isNaN(Number(v))
) as InvoiceStatus[];

const MultiSelectSmartFilter = ({ children, name, ...props }: WsFilterDialogProps) => {
  const form = useFormContext();
  const selected = form.watch(name) ?? [];

  return (
    <WsFilterDialog name={selected.length > 0 ? `selected ${selected.length}` : 'All'} {...props}>
      {children}
    </WsFilterDialog>
  );
};

const mapFormToDTO = ({
  invoiceStatus,
  search,
  clients,
  invoiceTypes,
  placementType,
}: InvoiceListingFiltersForm) => ({
  invoiceStatus:
    invoiceStatus?.map((status) => InvoiceStatus[status] as unknown as InvoiceStatus) ?? [],
  search: search && getFullTextSearchString(search),
  clientIds: clients?.map(({ id }) => id) ?? [],
  invoiceTypeIds: invoiceTypes?.map(({ id }) => id) ?? [],
  placementType,
});

interface InvoiceListingFiltersForm {
  invoiceTypes: IInvoiceType[];
  invoiceStatus: InvoiceStatus[];
  search: string;
  clients?: ClientDTO[];
  placementType: 'PERMANENT' | 'CONTRACTOR';
}

const InvoiceList = ({ placementType }: IInvoiceList) => {
  const form = useForm<InvoiceListingFiltersForm>({
    defaultValues: {
      invoiceTypes: [],
      invoiceStatus: [],
      placementType,
    },
  });
  const optionsData = useMemo(() => new Map(), []);
  const myMap = useMemo(() => new Map(), []);
  const [clientListData, setClientListData] = useState<InvoiceDTO[]>([]);
  const { getValues } = form;
  const [contractorInvoicePage, setContractorInvoicePage] = useState<Page<InvoiceListing>>();
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'invoiceCode', sort: 'asc' },
  ]);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const navigate = useNavigate();
  const [invoiceTypes, setInvoiceTypes] = useState<IInvoiceType[]>([]);
  const workspace = useWorkspaceContext();
  const tableData: Record<string, unknown>[] = [];

  const [settings, setSettings] = useState<GridColumnVisibilityModel | null>(null);
  const isAuthorizedViewInvoicingAll = useInvoicingAuthz(InvoicingAction.VIEW_INVOICE_LISTING_PAGE);
  const isAuthorizedAddInvoicing = useInvoicingAuthz(InvoicingAction.ADD_INVOICE_ADDITION);

  const fetchInvoicePage = useCallback(async () => {
    const filters = getValues();
    const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
    const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
    const response = await getInvoiceList({
      ...mapFormToDTO(filters),
      page: pageNo,
      size: pageSize,
      sortBy: sortBy,
      sortOrder: sortOrder,
    });
    if (placementType === 'PERMANENT') {
      const invoiceCandidateResList = await getInvoiceCandidateList();
      invoiceCandidateResList.data.forEach((data) => {
        const newTempValue = myMap.get(data?.clientInvoiceId);
        if (newTempValue) {
          newTempValue.push(data.name);
          myMap.set(data?.clientInvoiceId, newTempValue);
        } else {
          myMap.set(data?.clientInvoiceId, [data.name]);
        }

        const tempOptionsData = optionsData.get(data?.clientInvoiceId);
        if (tempOptionsData) {
          optionsData?.set(data?.clientInvoiceId, [
            data?.name,
            ...optionsData.get(data?.clientInvoiceId),
          ]);
        } else {
          optionsData.set(data.clientInvoiceId, data.name);
        }
      });

      setClientListData(response.data.content);
      setTotalRecord(response.data.totalElements);
    } else {
      setContractorInvoicePage(response.data);
    }
  }, [getValues, myMap, optionsData, pageNo, pageSize, placementType, sortModel]);

  useEffect(() => {
    getAllInvoiceTypes().then(setInvoiceTypes);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getColumnSettings(ColumnSettings.INVOICE_LISTING);
        setSettings(res.data.settings);
      } catch (error) {
        setSettings({
          clientInvoiceTypename: true,
          clientName: true,
          createdOn: true,
          dueDate: true,
          id: true,
          invoiceCode: true,
          invoiceStage: true,
          invoiceStatus: true,
          ourEntityName: false,
          pdfFile: true,
          raisedDate: true,
          remainingAmount: true,
          total: true,
        });
      }
    };
    if (settings === null) {
      fetchData();
    }
  }, [settings]);

  useEffect(() => {
    myMap.clear();
    fetchInvoicePage();
  }, [fetchInvoicePage, form, myMap]);

  const navigateToAddInvoice = () => navigate('/invoice/addoptions');
  const settingInvoiceStatus = (date: string) => {
    let status = 'NA';
    if (date) {
      const dayDiff = getDateDifferenceInDay(date, new Date().toDateString());
      if (dayDiff > 0) {
        status = `Due in ${dayDiff} Days`;
      } else if (dayDiff < 0) {
        status = `Overdue by ${dayDiff * -1} Days`;
      } else {
        status = 'Due Today';
      }
    }
    return status;
  };

  const onColumnChange = async (data: GridColumnVisibilityModel) => {
    const settingsPayload: ColumnSettingsDTO = {
      settings: data,
      tableKey: ColumnSettings.INVOICE_LISTING,
    };
    await saveColumnSettings(settingsPayload);
  };

  const getPdfDownload = async (params) => {
    const {
      data: { preSignedUrl },
    } = await getPresignedURLForGet({
      fileName: String(placementType === 'PERMANENT' ? params.pdfFile?.location : params.pdfFile),
      downloadAs: params?.invoiceCode ?? 'invoice',
    });
    window.open(preSignedUrl, '_blank');
  };

  const onSearch = async () => {
    myMap.clear();
    fetchInvoicePage();
  };

  clientListData?.map((item) => {
    tableData.push({
      id: item.id,
      clientName: item.client.name,
      invoiceCode: item.invoiceCode,
      clientInvoiceTypeName: item.clientInvoiceType.name,
      raisedDate:
        item?.raisedDate != undefined
          ? getDateUsingFormat(item.raisedDate?.toString(), 'DD-MMM-YY')
          : '',
      dueDate:
        item?.dueDate != undefined ? getDateUsingFormat(item.dueDate?.toString(), 'DD-MMM-YY') : '',
      invoiceStatus: settingInvoiceStatus(item.dueDate ? item.dueDate.toString() : ''),
      invoiceStage: item.invoiceStage,
      total: (item.currencyEntity?.symbol ?? '') + item.total,
      remainingAmount: (item.currencyEntity?.symbol ?? '') + item.remainingAmount,
      pdfFile: item.pdfFile,
      ourEnttityId: item.ourEntity?.id,
      ourEntityName: item.ourEntity?.name,
      clientId: item.client.id,
      createdOn:
        item?.client.createdOn != undefined
          ? getDateUsingFormat(item.client.createdOn?.toString(), 'DD-MMM-YY')
          : '',
    });
  });

  const columns: GridColDef[] = [
    {
      field: 'invoiceCode',
      headerName: 'Invoice #',
      flex: 1.5,
      renderCell: (params) => (
        <Link
          component={RouterLink}
          underline="none"
          to={
            params.row.invoiceStage === 'DRAFT'
              ? `/invoice/${params.row.id}/edit`
              : `/invoice/${params.row.id}`
          }
        >
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            cursor="pointer"
            bold
            color={WsTypoColors.PRIMARY}
          >
            {params.row['invoiceCode'] ?? 'NA'}
          </WsTypography>
        </Link>
      ),
    },
    {
      field: 'clientName',
      headerName: 'Client Name',
      flex: 1,
      renderCell: (params) => (
        <Link
          component={RouterLink}
          underline="none"
          to={`/client-details?clientId=${
            params.row.clientId
          }&breadCrumbList=${getEncodedQueryParams(ClintPageBreadCrumb['CLIENTS'])}`}
        >
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY}
            cursor="pointer"
          >
            {params.row['clientName']}
          </WsTypography>
        </Link>
      ),
    },
    {
      field: 'clientInvoiceTypeName',
      headerName: 'Invoice Category',
      flex: 1,
    },
    {
      field: 'ourEntityName',
      headerName: 'Organisation Entity',
      flex: 1,
    },
    {
      field: 'id',
      headerName: 'Candidate Placed',
      flex: 1,
      renderCell: (params) =>
        placementType === 'PERMANENT' ? (
          <WsTypography
            hoverText={myMap.get(params.row?.id)?.join(',') ?? ''}
            variant={WsTypoVariants.SUB_TITLE}
            cursor="pointer"
          >
            {!isEmpty(myMap.get(params.row?.id)) && myMap.get(params.row?.id).length <= 2
              ? myMap.get(params.row?.id)?.join(',') ?? ''
              : `${!isEmpty(myMap.get(params.row?.id)) ? myMap.get(params.row?.id)[0] : ''} ...`}
          </WsTypography>
        ) : (
          <WsTypography
            hoverText={params.row.candidates}
            variant={WsTypoVariants.SUB_TITLE}
            cursor="pointer"
          >
            {params.row.candidates.split(',').length <= 1
              ? params.row.candidates
              : params.row.candidates.split(',')[0]}
          </WsTypography>
        ),
    },
    {
      field: 'raisedDate',
      headerName: 'Date',
      flex: 0.8,
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      flex: 0.8,
    },
    {
      field: 'invoiceStatus',
      headerName: 'Payment Status',
      flex: 1,
    },
    {
      field: 'invoiceStage',
      headerName: 'Invoice Status',
      flex: 1,
    },
    {
      field: 'total',
      headerName: 'Invoice Amount',
      flex: 1,
    },
    {
      field: 'remainingAmount',
      headerName: 'Amount Due',
      flex: 1,
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 0.8,
    },
    {
      field: 'pdfFile',
      headerName: '',
      flex: 0.2,
      renderCell: (params) => (
        <WsTypography color={WsTypoColors.BLACK}>
          <WsIconText
            text=""
            textCursor="pointer"
            onClick={() => getPdfDownload(params.row)}
            icon={<DownloadIcon />}
          />
        </WsTypography>
      ),
    },
  ];

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSearch)}>
          <Grid container item rowSpacing={2} columnSpacing={1} xs={12}>
            <Grid item xs={10}>
              <ControlledTextInput
                fullWidth
                name="search"
                placeholder="Search Candidate, Job, Invoice ID..."
              />
            </Grid>
            <Grid item xs={1}>
              <WsButton
                fullWidth
                variant="text"
                size="large"
                type="reset"
                onClick={() => {
                  form.reset();
                  onSearch();
                }}
              >
                Reset
              </WsButton>
            </Grid>
            <Grid item xs={1}>
              <WsButton fullWidth size="large" type="submit">
                Search
              </WsButton>
            </Grid>
            <Grid item xs={2.5}>
              <MultiSelectSmartFilter
                label="Invoice Category"
                name="invoiceTypes"
                icon={<HiOutlineCog size={24} />}
              >
                <StyledSmartFilterPopover>
                  <Controller
                    name="invoiceTypes"
                    control={form.control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        multiple
                        fullWidth
                        defaultValue={[]}
                        size="small"
                        renderValue={(selected) =>
                          selected?.map((option) => option.name).join(', ') || 'Select some options'
                        }
                      >
                        {invoiceTypes.map((option) => (
                          <MenuItem value={option as any} key={option.id}>
                            <Checkbox size="small" checked={field.value.includes(option)} />
                            <ListItemText primary={option.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </StyledSmartFilterPopover>
              </MultiSelectSmartFilter>
            </Grid>
            <Grid item xs={2.5}>
              <MultiSelectSmartFilter
                label="Client Name"
                name="clients"
                icon={<HiOutlineOfficeBuilding size={24} />}
              >
                <StyledSmartFilterPopover>
                  <ClientAutocomplete name="clients" multiple />
                </StyledSmartFilterPopover>
              </MultiSelectSmartFilter>
            </Grid>
            <Grid item xs={2.5}>
              <MultiSelectSmartFilter
                label="Receipt Status"
                name="invoiceStatus"
                icon={<HiReceiptTax size={24} />}
              >
                <StyledSmartFilterPopover>
                  <Controller
                    name="invoiceStatus"
                    control={form.control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        multiple
                        fullWidth
                        defaultValue={[]}
                        size="small"
                        renderValue={(selected) =>
                          selected?.map((option) => invoiceStatusMap[option]).join(', ') ||
                          'Select some options'
                        }
                      >
                        {invoiceStatusArray.map((option) => (
                          <MenuItem value={option} key={option}>
                            <Checkbox size="small" checked={field.value.includes(option)} />
                            <ListItemText primary={invoiceStatusMap[option]} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </StyledSmartFilterPopover>
              </MultiSelectSmartFilter>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
      {isAuthorizedViewInvoicingAll &&
      settings != null &&
      (placementType === 'PERMANENT' ? tableData && tableData.length > 0 : true) ? (
        <Grid xs={12} sx={{ backgroundColor: 'white' }}>
          <DataGrid
            autoHeight={true}
            components={{ Toolbar: CustomToolbar }}
            columns={columns}
            rows={placementType === 'PERMANENT' ? tableData : contractorInvoicePage?.content ?? []}
            rowCount={
              placementType === 'PERMANENT' ? totalRecord : contractorInvoicePage?.totalElements
            }
            onPageChange={(page) => setPageNo(page)}
            rowsPerPageOptions={[25, 50, 75, 100]}
            page={pageNo}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            sortingMode="server"
            paginationMode="server"
            onSortModelChange={(newSortModel) => {
              setPageNo(0);
              setSortModel(newSortModel);
            }}
            sortModel={sortModel}
            columnVisibilityModel={settings}
            onColumnVisibilityModelChange={(data) => {
              onColumnChange(data);
              setSettings(data);
            }}
            getRowHeight={() => 'auto'}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
          />
        </Grid>
      ) : (
        <>
          {(workspace.owns || isAuthorizedAddInvoicing) && (
            <NoDataFoundCard
              text="No results found. Would you like to add a invoice?"
              icon={<EmptyTeamsAndUsersIcon />}
              subElement={
                <Box m={2}>
                  {
                    <WsButton
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={navigateToAddInvoice}
                    >
                      Create Invoice
                    </WsButton>
                  }
                </Box>
              }
            />
          )}
        </>
      )}
    </>
  );
};

export default InvoiceList;
